import type { TextFieldPropValue } from 'components/FieldNumberFormat';
import { FieldNumberFormat } from 'components/FieldNumberFormat';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type {
    WorkOrderChangeObjectItem,
    WorkOrderChangeOtherAggregationData,
    WorkOrderChangeOtherAggregationDataFilterInput,
    WorkOrderChangeOtherPriceInput,
} from 'gql/graphql';
import { OtherCostCalculationTypeEnum } from 'gql/graphql';
import { useTableCollapse } from 'hooks/useTableCollapse';
import Styles from 'pages/Project/UerProject/WorkOrder/AddOrderOther/AddOrderOther.module.css';
import { prepareOtherCostData } from 'pages/Project/UerProject/WorkOrder/AddOrderOther/addOtherHelpers';
import { getAddOthersBaseColumns } from 'pages/Project/UerProject/WorkOrder/AddOrderOther/addOthersBaseColumns';
import { getInitialChecked } from 'pages/Project/UerProject/WorkOrder/AddOrderOther/AddOthersTable';
import type { ICheckedObjs, IOthersObject, IOtherValues } from 'pages/Project/UerProject/WorkOrder/AddOrderOther/types';
import { useAddOtherData } from 'pages/Project/UerProject/WorkOrder/AddOrderOther/useAddOtherData';
import type { AnyObject } from 'types/AnyObject';
import { Checkbox, ChoiceGroup, TextField, withTooltip } from 'uikit';
import { replacerCost } from 'utils/replacerCost';

import { noun } from 'plural-ru';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getWhere, renameVariablesForWorkOrderChange } from '../helpers';
import { getOtherToWorkOrderChangePriceList } from '../WorkOrderChangeQueries';

type TOtherItemWithIds = WorkOrderChangeOtherAggregationData & {
    nestLevel: number;
    parentId: number;
    objectId: number;
    id: number;
};

const CheckboxWithToolTip = withTooltip({
    mode: 'mouseover',
    // @ts-ignore
})(Checkbox);

const choiceItems: { label: string; isIncluded: boolean }[] = [
    { label: 'Да', isIncluded: true },
    {
        label: 'Нет',
        isIncluded: false,
    },
];

export const useAddOtherToWorkOrderChangeTableData = ({
    workOrderChangeId,
    constructionTypeId,
    workOrderChangeObjectsData,
    costRounding,
}: {
    workOrderChangeId: number;
    constructionTypeId: number;
    workOrderChangeObjectsData: WorkOrderChangeObjectItem[];
    costRounding: number;
}) => {
    const dispatch = useDispatch();
    const [visibleTableData, setVisibleTableData] = useState<AnyObject[]>([]);
    const [checkedIds, setCheckedIds] = useState<ICheckedObjs>({});
    const [valueNote, setValueNote] = useState<Record<string, string>>({});
    const [otherPriceValues, setOtherPriceValues] = useState<IOtherValues>({});
    const [createdOtherId, setCreatedOtherId] = useState<number | null>(null);
    const [filters, setFilters] = useState({});

    const variables = useMemo(
        () => ({
            take: 1000,
            skip: 0,
            workOrderChangeId,
            constructionTypeId,
            where: getWhere<WorkOrderChangeOtherAggregationDataFilterInput>(filters),
        }),
        [constructionTypeId, filters, workOrderChangeId],
    );

    const [othersToWorkOrderChangeData, othersToWorkOrderChangeStatus, , othersToWorkOrderChangeRequest] = useRequest({
        requestId: 'othersToWorkOrderChange',
        query: getOtherToWorkOrderChangePriceList,
        withEffect: !!(workOrderChangeId && constructionTypeId),
        variables,
    });

    const [makeResetData] = useResetStoreData({
        requestId: 'othersToWorkOrderChange',
    });

    useEffect(
        () => () => {
            makeResetData(undefined);
        },
        [makeResetData],
    );

    const othersWithConstructionTypes = useAddOtherData({
        workOrderOthers: visibleTableData,
        workOrderObjects: (workOrderChangeObjectsData as IOthersObject[]) ?? [],
        constructionTypeId,
    });

    useEffect(() => {
        if (othersToWorkOrderChangeData?.uerWorkOrderChangeOtherListQuery?.items) {
            setVisibleTableData(othersToWorkOrderChangeData?.uerWorkOrderChangeOtherListQuery?.items);
        }
    }, [othersToWorkOrderChangeData?.uerWorkOrderChangeOtherListQuery?.items]);

    const addOthersBaseColumns = useMemo(() => getAddOthersBaseColumns(costRounding), [costRounding]);

    const [preparedColumns, preparedRows, , collapseAll] = useTableCollapse(
        'addOtherToWorkOrderChange',
        addOthersBaseColumns,
        othersWithConstructionTypes,
    );

    useEffect(() => {
        if (preparedRows.length && !Object.keys(checkedIds).length) {
            const initialChecked = getInitialChecked(othersWithConstructionTypes);

            setCheckedIds(initialChecked);
            collapseAll()();
        }
    }, [checkedIds, collapseAll, othersWithConstructionTypes, preparedRows.length]);

    const resetOtherSeasons = useCallback(
        (row) =>
            row?.limits?.reduce((acc: Record<number, AnyObject>, limit: { season: { id: number } }) => {
                if (limit?.season?.id) {
                    // eslint-disable-next-line prefer-destructuring
                    acc[limit.season.id] = choiceItems[1];
                }

                return acc;
            }, {}),
        [],
    );

    const handleChangeSeasonValues = useCallback(
        (otherId: number | string, seasonId: number, isNumberField = false) =>
            ({ value }: any) => {
                if (value === '-' || !value) {
                    return;
                }

                const valueForSave = !isNumberField ? { ...value } : { includedCost: value };

                setOtherPriceValues((ids) => ({
                    ...ids,
                    [otherId]: {
                        ...ids?.[otherId],
                        [seasonId]: {
                            ...valueForSave,
                        },
                    },
                }));
            },
        [],
    );

    const handleChangeCheckbox = useCallback(
        (row: TOtherItemWithIds) =>
            ({ checked }: { checked: boolean }) => {
                if (!row.parentId) {
                    const checkedChildren = othersWithConstructionTypes
                        ?.find((item) => item?.id === row.id)
                        ?.rows?.reduce((acc: Record<number, boolean>, child) => {
                            acc[child.objectId] = checked;

                            return acc;
                        }, {});

                    if (row.limits) {
                        const defaultSeasonPriceValues = resetOtherSeasons(row);

                        setOtherPriceValues((prev) => ({
                            ...prev,
                            [row.id]: defaultSeasonPriceValues || {},
                        }));
                    }

                    setCheckedIds((ids) => ({
                        ...ids,
                        [row.id]: {
                            parent: checked,
                            ...checkedChildren,
                        },
                    }));
                } else {
                    setCheckedIds((ids) => {
                        const childrenCounter = Object.entries(ids[row.parentId] || {}).length - 1;
                        const prevCheckedCount = Object.entries(ids[row.parentId] || {})?.filter(
                            ([id, idValue]) => id !== 'parent' && idValue,
                        ).length;
                        const checkedCount = checked ? prevCheckedCount + 1 : prevCheckedCount - 1;
                        const countsEqual = checkedCount === childrenCounter;
                        const parentState = countsEqual || checkedCount === 0 ? checked : 'intermediate';

                        if (countsEqual) {
                            const defaultSeasonPriceValues = resetOtherSeasons(
                                othersWithConstructionTypes.find(
                                    (dataRow) => dataRow.contractOtherCostId === Number(row.parentId),
                                ),
                            );

                            setOtherPriceValues((prev) => ({
                                ...prev,
                                [row.parentId]: defaultSeasonPriceValues || {},
                            }));
                        }

                        return {
                            ...ids,
                            [row.parentId]: {
                                ...ids?.[row.parentId],
                                parent: parentState,
                                [row.objectId]: checked,
                            },
                        };
                    });
                }
            },

        [othersWithConstructionTypes, resetOtherSeasons],
    );

    const handleChangesValueNote = useCallback(
        (rowId: number) =>
            ({ value }: { value: TextFieldPropValue }) => {
                setValueNote((ids) => ({ ...ids, [rowId]: value }));
            },
        [],
    );

    const columns = useMemo(
        () => [
            {
                title: '',
                accessor: 'checkbox',
                width: 40,
                withoutPadding: true,
                renderCell: (row: TOtherItemWithIds) => {
                    const tooltipCounterObj =
                        checkedIds?.[row.id] &&
                        Object.entries(checkedIds[row.id]).reduce(
                            (acc, [id, value]) => {
                                if (id !== 'parent') {
                                    acc.totalCount += 1;
                                    if (value) {
                                        acc.checkedCount += 1;
                                    }
                                }

                                return acc;
                            },
                            { checkedCount: 0, totalCount: 0 },
                        );

                    return (
                        <div style={{ margin: '16px 12px' }}>
                            {row.nestLevel === 0 ? (
                                <CheckboxWithToolTip
                                    // @ts-ignore
                                    checked={!!checkedIds?.[row?.id]?.parent}
                                    onChange={handleChangeCheckbox(row)}
                                    intermediate={checkedIds?.[row.id]?.parent === 'intermediate'}
                                    tooltipProps={{
                                        content: `${tooltipCounterObj?.checkedCount}/${tooltipCounterObj?.totalCount}
                                        ${noun(tooltipCounterObj?.totalCount, ...['объекта', 'объектов'])}`,
                                        className: Styles.tooltip,
                                    }}
                                />
                            ) : (
                                <Checkbox
                                    checked={checkedIds?.[row?.parentId]?.[row?.objectId]}
                                    onChange={handleChangeCheckbox(row)}
                                />
                            )}
                        </div>
                    );
                },
            },
            ...preparedColumns,
            {
                title: 'Включить в ИЗМ. к НЗ',
                accessor: 'addToWorkOrderChange',
                width: 100,

                renderCell: (row: TOtherItemWithIds) => (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', width: '100%' }}>
                        {row?.limits?.map((limit) => {
                            const isDisabled = !checkedIds[row.id]?.parent;

                            const currentId = `${row.id}_${limit?.season?.id}`;
                            const currentSeasonRemainderCost = limit?.totalRemainderCost;

                            if (row.calculatingType === OtherCostCalculationTypeEnum.Calculation) {
                                const inputValue = otherPriceValues?.[row.id]?.[limit?.season?.id]?.includedCost;
                                const isDeviated = replacerCost(inputValue) > currentSeasonRemainderCost;

                                return (
                                    <FieldNumberFormat
                                        key={currentId}
                                        value={inputValue}
                                        decimalScale={costRounding}
                                        size="xs"
                                        onChange={handleChangeSeasonValues(row.id, limit?.season?.id, true)}
                                        disabled={isDisabled}
                                        leftSide={isDeviated ? '▲' : undefined}
                                        className={Styles.editableField}
                                    />
                                );
                            }
                            if (row.calculatingType === OtherCostCalculationTypeEnum.Percent) {
                                return (
                                    <ChoiceGroup
                                        key={currentId}
                                        value={otherPriceValues?.[row.id]?.[limit?.season?.id] ?? choiceItems[1]}
                                        onChange={handleChangeSeasonValues(row.id, limit?.season?.id)}
                                        items={choiceItems}
                                        getItemLabel={({ label }) => label}
                                        multiple={false}
                                        name="addToWorkOrderChange"
                                        size="xs"
                                        style={{ marginBottom: 10 }}
                                        disabled={isDisabled}
                                    />
                                );
                            }

                            return null;
                        })}
                    </div>
                ),
            },
            {
                title: 'Примечание',
                accessor: 'note',
                width: 160,
                renderCell: (row: TOtherItemWithIds & { withCollapseButton: boolean }) => {
                    if (!row?.withCollapseButton) {
                        return null;
                    }

                    return (
                        <div>
                            <TextField
                                value={valueNote[row.id]}
                                onChange={handleChangesValueNote(row.id)}
                                disabled={!checkedIds?.[row?.id]?.parent}
                                placeholder="Введите примечание"
                                size="s"
                            />
                        </div>
                    );
                },
            },
        ],
        [
            checkedIds,
            costRounding,
            handleChangeCheckbox,
            handleChangeSeasonValues,
            handleChangesValueNote,
            otherPriceValues,
            preparedColumns,
            valueNote,
        ],
    );

    const variablesForAdd = useMemo(() => {
        const otherPrices = prepareOtherCostData({
            checkedObjects: checkedIds,
            priceValues: otherPriceValues,
            valueNote,
        });

        const otherPricesWithRenamedKey = renameVariablesForWorkOrderChange(otherPrices);

        otherPricesWithRenamedKey?.forEach((price: any) => {
            price.objectIdsList = price?.objectIdsList?.map((id: number) => {
                const { workOrderChangeObjectId, workOrderObjectId } =
                    workOrderChangeObjectsData?.find((d) => d.id === id) || {};

                return { workOrderChangeObjectId, workOrderObjectId };
            });
        });

        return {
            input: {
                workOrderChangeId,
                constructionTypeId,
                otherPrices: otherPricesWithRenamedKey as unknown as WorkOrderChangeOtherPriceInput[],
            },
        };
    }, [checkedIds, constructionTypeId, otherPriceValues, valueNote, workOrderChangeId, workOrderChangeObjectsData]);

    const btnConfirmDisabled = useMemo(() => !Object.values(checkedIds).some((item) => item.parent), [checkedIds]);
    const haveCheckedOthers = Object.values(checkedIds).some((checkedId) => checkedId.parent);

    const handleTableFilterUpdated = useCallback((selectedFilters: any) => {
        setFilters(() => ({
            name: selectedFilters.name?.value,
        }));
    }, []);

    const reloadTableAfterCreate = useCallback(
        (id: number) => {
            setCreatedOtherId(id);
            dispatch(othersToWorkOrderChangeRequest(variables));
        },
        [dispatch, othersToWorkOrderChangeRequest, variables],
    );

    const isTableLoaded = useMemo(
        () => othersToWorkOrderChangeStatus === RequestStatus.SUCCESS,
        [othersToWorkOrderChangeStatus],
    );

    useEffect(() => {
        if (isTableLoaded && createdOtherId) {
            const row = preparedRows.find((d) => d.id === createdOtherId);

            if (row) {
                handleChangeCheckbox(row)({ checked: true });
                setCreatedOtherId(null);
            }
        }
    }, [createdOtherId, handleChangeCheckbox, isTableLoaded, preparedRows]);

    return {
        tableRows: preparedRows || [],
        getTableRowsStatus: othersToWorkOrderChangeStatus,
        tableColumns: columns,
        variablesForAdd,
        btnConfirmDisabled,
        haveCheckedOthers,
        handleFilterChange: handleTableFilterUpdated,
        reloadTableAfterCreate,
    };
};
